import React, { useState } from "react";
import { journey, getStory } from "../../data/impactStories";
import { FileText, X } from "lucide-react";

const statusCls = {
  Completed: "bg-[#F5D000] text-black",
  "On Track": "bg-[#F5D000]/15 text-[#F5D000] border border-[#F5D000]/40",
  Planned: "bg-white/5 text-white/60 border border-white/15"
};

export default function RiJourney({ onOpenStory }) {
  const [open, setOpen] = useState(null);
  const active = journey.find((j) => j.id === open);

  return (
    <section id="journey" data-testid="ri-journey" className="py-24 lg:py-32 bg-[#08080a] scroll-mt-20 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-16 reveal">
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-4">The Journey</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02]">
            Seven decades in.<br /><span className="text-amber-accent">Just getting started.</span>
          </h2>
        </div>
      </div>

      {/* Horizontal journey */}
      <div className="relative">
        <div className="overflow-x-auto no-scrollbar pb-6" data-testid="ri-journey-track">
          <div className="flex items-start gap-0 min-w-max px-6 lg:px-[max(2.5rem,calc((100vw-1400px)/2+2.5rem))]">
            {journey.map((m, i) => (
              <div key={m.id} className="reveal relative flex flex-col items-center w-44 sm:w-52 shrink-0" style={{ transitionDelay: `${i * 110}ms` }}>
                {/* connecting line */}
                {i < journey.length - 1 && (
                  <div className="absolute top-[38px] left-1/2 w-full h-px bg-gradient-to-r from-[#F5D000]/50 to-white/10" />
                )}
                <button
                  data-testid={`journey-node-${m.id}`}
                  onClick={() => setOpen(open === m.id ? null : m.id)}
                  className={`journey-node relative z-10 w-[76px] h-[76px] rounded-full border-2 flex items-center justify-center font-display font-bold transition-all duration-500 ${
                    open === m.id
                      ? "bg-[#F5D000] border-[#F5D000] text-black scale-110"
                      : "bg-[#0e0e12] border-white/20 text-white hover:border-[#F5D000] hover:scale-105"
                  }`}
                >
                  <span className="text-sm">{m.year}</span>
                </button>
                <div className="mt-4 text-center px-2">
                  <div className="text-sm font-semibold text-white leading-snug">{m.title}</div>
                  <span className={`inline-block mt-2 px-2.5 py-0.5 rounded-full text-[9px] uppercase tracking-[0.16em] font-bold ${statusCls[m.status]}`}>
                    {m.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Expanded panel */}
        {active && (
          <div data-testid="journey-panel" className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-6 ri-fade-up">
            <div className="rounded-2xl bg-[#101014] border border-[#F5D000]/25 overflow-hidden grid grid-cols-1 md:grid-cols-12">
              <div className="md:col-span-4 h-52 md:h-auto">
                <img src={active.image} alt={active.title} className="w-full h-full object-cover" />
              </div>
              <div className="md:col-span-8 p-7 lg:p-9 relative">
                <button
                  data-testid="journey-panel-close"
                  onClick={() => setOpen(null)}
                  className="absolute top-5 right-5 w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#F5D000] mb-2">{active.year} · {active.status}</div>
                <h3 className="font-display text-2xl lg:text-3xl font-bold text-white mb-3">{active.title}</h3>
                <p className="text-white/70 leading-relaxed max-w-2xl mb-5">{active.text}</p>
                <div className="flex flex-wrap items-center gap-3">
                  {active.docs.map((d) => (
                    <span key={d} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-xs text-white/70">
                      <FileText className="w-3.5 h-3.5 text-[#F5D000]" /> {d}
                    </span>
                  ))}
                  {active.storyId && (
                    <button
                      data-testid="journey-open-story"
                      onClick={() => onOpenStory(getStory(active.storyId))}
                      className="btn-amber px-4 py-2 rounded-md text-xs font-semibold"
                    >
                      Read the Impact Story
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
